/**
 * Pure column-reorder math shared by the header drag-reorder (`FlowTable`)
 * and the `ColumnsPopover` "Shown" list drag.
 *
 * `side` is the half of the drop-target the pointer was over when the drop
 * fired: `'before'` places the dragged key immediately ahead of `toKey`,
 * `'after'` immediately behind it — including the last position, when the
 * drop lands on the right half of the last column. This is the single
 * source of truth for the splice math so the visual drop indicator (drawn
 * from the same `side` value) can never disagree with where the item
 * actually lands.
 *
 * `sideForPointer` is the shared source of `side` for both drag indicators
 * (the header drag in `useHeaderDragResize` and the Columns manager list),
 * so the two surfaces resolve the pointer half identically.
 */
export type ColumnDragSide = 'before' | 'after';

/**
 * Which half of `rect` the pointer is over along `axis`. Total: a rect with no
 * extent on that axis (jsdom, hidden element) compares the pointer against its
 * start. Exact midpoint resolves to `'after'`.
 */
export function sideForPointer(
  rect: { left: number; top: number; width: number; height: number },
  point: { x: number; y: number },
  axis: 'x' | 'y',
): ColumnDragSide {
  const start = axis === 'x' ? rect.left : rect.top;
  const extent = axis === 'x' ? rect.width : rect.height;
  const pos = axis === 'x' ? point.x : point.y;
  return pos < start + extent / 2 ? 'before' : 'after';
}

export function reorderColumns(
  order: readonly string[],
  fromKey: string,
  toKey: string,
  side: ColumnDragSide,
): string[] {
  if (fromKey === toKey) return [...order];
  const fromIdx = order.indexOf(fromKey);
  const toIdx = order.indexOf(toKey);
  if (fromIdx === -1 || toIdx === -1) return [...order];

  const next = [...order];
  next.splice(fromIdx, 1);
  // Re-locate the target *after* removing the dragged item — removing an
  // earlier element shifts every later index down by one, which is exactly
  // the case (a rightward drag) the old code got wrong by reusing the
  // pre-removal index.
  const targetIdx = next.indexOf(toKey);
  const insertIdx = side === 'before' ? targetIdx : targetIdx + 1;
  next.splice(insertIdx, 0, fromKey);
  return next;
}
